import React, { useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import DocumentUploader from '../../../../Form/DocumentUploader';
import DocumentForm from './DocumentForm';
import Button from '../../../../Button';
import DocumentsTable from './DocumentsTable';

import { useUiContext } from '../../../../../context/UiContext';
import DocumentService from '../../../../../services/DocumentService';
import LenderService from '../../../../../services/LenderService';

export default function Documents({ loanId, readOnly, lenderId }) {
  const [documents, setDocuments] = useState([]);
  const [documentRefs, setDocumentRefs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useUiContext();

  const { data: fetchedLoanDocuments, refetch: refetchLoanDocuments } =
    useQuery({
      queryKey: ['loanDocuments', loanId],
      queryFn: readOnly
        ? () => DocumentService.getLoanDocumentsForLender(loanId)
        : () =>
            DocumentService.getLoanDocuments({
              loanId,
              category: 'user',
              status: ['accepted', 'pending'],
            }),
      enabled: !!loanId,
    });

  const { data: fetchedLenderDocuments, refetch: refetchLenderDocuments } =
    useQuery({
      queryKey: ['fetchedLenderDocuments', lenderId],
      queryFn: () => LenderService.getLenderDocuments(lenderId),
      enabled: !!lenderId,
    });

  useEffect(() => {
    // Initialize refs for each document
    setDocumentRefs(documents.map(() => React.createRef()));
  }, [documents]);

  // Submit all documents
  const submitAll = () => {
    // Check if there are any errors in the document forms
    const hasErrors = documentRefs.some(
      (ref) => Object.keys(ref.current.formik.errors).length > 0
    );

    // If there are errors set all document forms to touched
    if (hasErrors) {
      documentRefs.forEach((ref) => {
        ref.current.formik.setTouched({
          name: true,
          type: true,
          description: true,
        });
      });
      return;
    }

    // If no errors submit all documents
    documentRefs.forEach((ref) => {
      ref.current.formik.submitForm();
      setIsSubmitting(true);
    });

    const timer = setInterval(() => {
      // Check if all documents have been submitted
      const allSubmitted = documentRefs?.every?.(
        (ref) => !ref.current.formik.isSubmitting
      );

      if (allSubmitted) {
        // If all documents have been submitted, clear the interval
        clearInterval(timer);

        if (lenderId) {
          refetchLenderDocuments();
        } else {
          refetchLoanDocuments();
        }
        showToast({
          text: 'Uploaded successfully',
          type: 'success',
        });
        setDocuments([]);
        setIsSubmitting(false);
      }
    }, 50);
  };

  return (
    <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      {!readOnly && (
        <>
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
            Documents
          </h2>
          <DocumentUploader
            loanDocuments
            setValue={(file) =>
              setDocuments((prevState) => {
                // Check if the file already exists in the array
                const fileExists = prevState.some(
                  (prevFile) =>
                    prevFile.name === file.name &&
                    prevFile.size === file.size &&
                    prevFile.lastModified === file.lastModified
                );

                // If the file doesn't exist, add it to the array
                if (!fileExists) {
                  return [...prevState, file];
                }

                // If the file exists, just return the previous state
                return prevState;
              })
            }
          />
        </>
      )}
      <div className={readOnly ? '' : 'mt-10'}>
        {documents.map((doc, index) => (
          <DocumentForm
            lenderId={lenderId}
            ref={documentRefs[index]}
            id={doc.size}
            loanId={loanId}
            key={doc.size}
            file={doc}
            deleteDocument={() => {
              setDocuments((prevState) =>
                prevState.filter((prevDoc) => prevDoc.size !== doc.size)
              );
            }}
          />
        ))}
      </div>
      {documents.length > 0 && (
        <div className="flex justify-end mb-10">
          <Button type="submit" onClick={submitAll} isSubmitting={isSubmitting}>
            Upload
          </Button>
        </div>
      )}
      <DocumentsTable
        rows={lenderId ? fetchedLenderDocuments : fetchedLoanDocuments}
        refetchDocuments={
          lenderId ? refetchLenderDocuments : refetchLoanDocuments
        }
        readOnly={readOnly}
        lenderId={lenderId}
      />
    </div>
  );
}
